import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersAPI, paymentsAPI, poAPI, qcAPI, alterationsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  CreditCard, FileText, Package, Truck, CheckCircle2, AlertTriangle, XCircle,
  ClipboardCheck, Scissors, RotateCcw, Play, Eye, Copy, Mail,
  ChevronRight, Loader2, Calendar, Clock, HardHat, Send,
} from 'lucide-react';
import OrderPayments from '@/components/OrderPayments';
import AlterationsTracker from '@/components/AlterationsTracker';

const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta',
  pyjama: 'Pyjama', waistcoat: 'Waistcoat',
};

const TASK_TYPE_LABELS = {
  pattern_draft: 'Pattern', pattern_cut: 'Cutting', cutting: 'Cutting', stitching: 'Stitching',
  finishing: 'Finishing', outsource: 'Outsource',
};

const TASK_STATUS_ICONS = {
  completed: { icon: CheckCircle2, color: '#059669' },
  in_progress: { icon: Play, color: '#D97706' },
  pending: { icon: Clock, color: '#9CA3AF' },
  assigned: { icon: Clock, color: '#6B7280' },
};

export default function WhatsNextBar({ order, onRefresh }) {
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [qcSheetOpen, setQcSheetOpen] = useState(false);
  const [qcTarget, setQcTarget] = useState(null);
  const [alterationSheetOpen, setAlterationSheetOpen] = useState(false);
  const [deliverySheetOpen, setDeliverySheetOpen] = useState(false);

  const fetchContext = useCallback(async () => {
    try {
      const res = await ordersAPI.workflowContext(order.id);
      setCtx(res.data);
    } catch (err) {
      console.error('Failed to fetch workflow context', err);
    } finally {
      setLoading(false);
    }
  }, [order.id]);

  useEffect(() => { fetchContext(); }, [fetchContext]);

  const refreshAll = () => {
    fetchContext();
    onRefresh?.();
  };

  if (loading || !ctx) return null;
  if (order.status === 'draft' || order.status === 'cancelled') return null;

  const cs = order.currency === 'INR' ? '\u20B9' : order.currency === 'USD' ? '$' : order.currency === 'GBP' ? '\u00A3' : `${order.currency} `;
  const { payment, procurement, production, qc, alterations: altCtx } = ctx;

  return (
    <>
      <div className="rounded-xl overflow-hidden" data-testid="whats-next-bar"
        style={{ border: '1.5px solid rgba(197,165,90,0.35)', background: 'linear-gradient(135deg, rgba(197,165,90,0.06) 0%, rgba(27,42,74,0.03) 100%)' }}>
        <div className="px-4 py-3 sm:px-5 sm:py-4">
          {/* Section header */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-5 rounded-full" style={{ backgroundColor: 'var(--brand-gold)' }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--brand-navy)' }}>
              What's Next
            </span>
          </div>

          {/* Status-specific content */}
          {order.status === 'confirmed' && <ConfirmedActions ctx={ctx} cs={cs} order={order} onPayment={() => setPaymentSheetOpen(true)} navigate={navigate} />}
          {order.status === 'fabric_ordered' && <FabricOrderedActions ctx={ctx} cs={cs} order={order} onRefresh={refreshAll} />}
          {order.status === 'fabric_received' && <FabricReceivedActions ctx={ctx} order={order} navigate={navigate} />}
          {order.status === 'in_production' && <InProductionActions ctx={ctx} order={order} onQC={(target) => { setQcTarget(target); setQcSheetOpen(true); }} />}
          {order.status === 'ready_for_trial' && <ReadyForTrialActions ctx={ctx} order={order} onAlteration={() => setAlterationSheetOpen(true)} onRefresh={refreshAll} />}
          {order.status === 'alterations_in_progress' && <AlterationsActions ctx={ctx} order={order} onQC={(target) => { setQcTarget(target); setQcSheetOpen(true); }} onRefresh={refreshAll} />}
          {order.status === 'ready_for_delivery' && <ReadyForDeliveryActions ctx={ctx} cs={cs} order={order} onPayment={() => setPaymentSheetOpen(true)} onDelivery={() => setDeliverySheetOpen(true)} />}
          {order.status === 'delivered' && <DeliveredActions ctx={ctx} cs={cs} order={order} onPayment={() => setPaymentSheetOpen(true)} />}
        </div>
      </div>

      {/* Payment Sheet */}
      <Sheet open={paymentSheetOpen} onOpenChange={setPaymentSheetOpen}>
        <SheetContent className="sm:max-w-[480px] bg-white overflow-y-auto" data-testid="payment-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>Record Payment — {order.order_number}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <OrderPayments orderId={order.id} orderCurrency={order.currency} totalPrice={order.total_price}
              onPaymentRecorded={() => { refreshAll(); setPaymentSheetOpen(false); }} />
          </div>
        </SheetContent>
      </Sheet>

      {/* QC Sheet */}
      <Sheet open={qcSheetOpen} onOpenChange={setQcSheetOpen}>
        <SheetContent className="sm:max-w-[520px] bg-white overflow-y-auto" data-testid="qc-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>
              QC Inspection — {qcTarget?.garment_type ? GARMENT_LABELS[qcTarget.garment_type] : ''}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            {qcTarget && qcSheetOpen && (
              <InlineQCForm
                orderId={order.id}
                orderItemId={qcTarget.order_item_id}
                garmentType={qcTarget.garment_type}
                qcType={qcTarget.qc_type || 'pre_trial'}
                onComplete={() => { refreshAll(); setQcSheetOpen(false); }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Alteration Sheet */}
      <Sheet open={alterationSheetOpen} onOpenChange={setAlterationSheetOpen}>
        <SheetContent className="sm:max-w-[520px] bg-white overflow-y-auto" data-testid="alteration-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>Record Trial — {order.order_number}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <AlterationsTracker
              orderId={order.id}
              orderItems={order.items}
              orderStatus={order.status}
              onRefresh={() => { refreshAll(); setAlterationSheetOpen(false); }}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Delivery Sheet */}
      <Sheet open={deliverySheetOpen} onOpenChange={setDeliverySheetOpen}>
        <SheetContent className="sm:max-w-[420px] bg-white overflow-y-auto" data-testid="delivery-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>Record Delivery — {order.order_number}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <DeliveryForm orderId={order.id} onComplete={() => { refreshAll(); setDeliverySheetOpen(false); }} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/* ============================================================
   STATUS: confirmed
   ============================================================ */
function ConfirmedActions({ ctx, cs, order, onPayment, navigate }) {
  const { payment, procurement } = ctx;
  const advancePaid = payment.total_paid > 0;
  const hasPos = procurement.pos.length > 0;
  const allComplete = advancePaid && hasPos;

  if (allComplete) {
    return (
      <CompleteSummary text={`Advance received (${cs}${payment.total_paid.toLocaleString('en-IN')}), PO ${procurement.pos[0]?.po_number || ''} generated — waiting for fabric.`} />
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-2" data-testid="confirmed-actions">
      {!advancePaid ? (
        <ActionButton icon={CreditCard} label="Collect Advance Payment" onClick={onPayment}
          sublabel={payment.next_milestone ? `${cs}${payment.next_milestone.amount?.toLocaleString('en-IN')} (${payment.next_milestone.label})` : null}
          testId="btn-collect-advance" />
      ) : (
        <DoneChip label={`Advance Received ${cs}${payment.total_paid.toLocaleString('en-IN')}`} />
      )}

      {!hasPos ? (
        <ActionButton icon={FileText} label="Generate PO" onClick={() => navigate('/app/invoices')}
          sublabel={procurement.pending_po_items > 0 ? `${procurement.pending_po_items} item(s) pending` : null}
          testId="btn-generate-po" />
      ) : (
        <div className="flex items-center gap-2">
          <DoneChip label={`PO ${procurement.pos[0]?.po_number}`} />
          <CopyWhatsAppButton poId={procurement.pos[0]?.id} />
        </div>
      )}
    </div>
  );
}

/* ============================================================
   STATUS: fabric_ordered
   ============================================================ */
function FabricOrderedActions({ ctx, order, onRefresh }) {
  const { procurement } = ctx;
  const { total_po_lines, received_po_lines } = procurement;
  const allReceived = total_po_lines > 0 && received_po_lines >= total_po_lines;

  if (allReceived) {
    return <CompleteSummary text="All fabrics received — ready for production scheduling." />;
  }

  return (
    <div className="space-y-2" data-testid="fabric-ordered-actions">
      <p className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
        Fabric receipt: {received_po_lines} of {total_po_lines} items received
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        {procurement.pos.map(po => (
          <ActionButton key={po.id} icon={Package} label={`Mark Fabric Received`}
            sublabel={`${po.po_number} — ${po.vendor_name}`}
            onClick={() => window.location.href = `/app/invoices/${po.id}`}
            testId={`btn-receive-${po.id}`} />
        ))}
        {procurement.pos.length === 0 && (
          <ActionButton icon={Package} label="View Procurement" onClick={() => window.location.href = '/app/invoices'}
            testId="btn-view-procurement" />
        )}
      </div>
      <ProgressDots total={total_po_lines} completed={received_po_lines} />
    </div>
  );
}

/* ============================================================
   STATUS: fabric_received
   ============================================================ */
function FabricReceivedActions({ ctx, order, navigate }) {
  const { production } = ctx;
  const hasTasks = production.total_tasks > 0;

  return (
    <div className="flex flex-col sm:flex-row gap-2" data-testid="fabric-received-actions">
      <ActionButton icon={HardHat} label={hasTasks ? 'View Production Schedule' : 'Assign to Workers'}
        sublabel={hasTasks ? `${production.total_tasks} tasks created` : 'Create production tasks'}
        onClick={() => navigate('/app/production')}
        testId="btn-production" />
      {hasTasks && (
        <CompactTaskList taskGroups={production.task_groups} />
      )}
    </div>
  );
}

/* ============================================================
   STATUS: in_production
   ============================================================ */
function InProductionActions({ ctx, order, onQC }) {
  const { production, qc } = ctx;
  const groups = production.task_groups;
  const items = order.items || [];

  // Check if all stitching is complete for each item
  const itemsNeedingQC = [];
  for (const item of items) {
    const group = groups[item.id];
    if (!group) continue;
    const stitching = group.tasks.find(t => t.task_type === 'stitching');
    const finishing = group.tasks.find(t => t.task_type === 'finishing');
    const allDone = group.tasks.every(t => t.status === 'completed' || t.status === 'qc_passed');
    const stitchingDone = stitching && (stitching.status === 'completed' || stitching.status === 'qc_passed');
    const hasPreTrialQC = qc.inspections.some(q => q.order_item_id === item.id && q.qc_type === 'pre_trial');
    if (stitchingDone && !hasPreTrialQC) {
      itemsNeedingQC.push(item);
    }
  }

  return (
    <div className="space-y-3" data-testid="in-production-actions">
      {/* Mini production cards per garment */}
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map(item => {
          const group = groups[item.id];
          if (!group) return null;
          return (
            <div key={item.id} className="rounded-lg p-2.5"
              style={{ backgroundColor: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.06)' }}
              data-testid={`production-card-${item.id}`}>
              <p className="text-xs font-bold mb-1.5" style={{ color: 'var(--brand-navy)' }}>
                {GARMENT_LABELS[item.garment_type] || item.garment_type}
              </p>
              <div className="flex items-center gap-1 flex-wrap">
                {group.tasks.sort((a, b) => {
                  const order = ['pattern_draft', 'cutting', 'stitching', 'finishing'];
                  return order.indexOf(a.task_type) - order.indexOf(b.task_type);
                }).map((task, i) => {
                  const statusCfg = TASK_STATUS_ICONS[task.status] || TASK_STATUS_ICONS.pending;
                  const Icon = statusCfg.icon;
                  const isActive = task.status === 'in_progress';
                  return (
                    <span key={task.id || i} className="flex items-center gap-0.5">
                      {i > 0 && <ChevronRight className="w-2.5 h-2.5" style={{ color: '#D1D5DB' }} />}
                      <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded ${isActive ? 'font-bold' : ''}`}
                        style={{
                          backgroundColor: isActive ? 'rgba(245,158,11,0.12)' : 'transparent',
                          color: statusCfg.color,
                        }}>
                        <Icon className="w-2.5 h-2.5" />
                        {TASK_TYPE_LABELS[task.task_type] || task.task_type}
                        {isActive && task.assigned_worker_name && (
                          <span className="text-[9px] font-normal ml-0.5">({task.assigned_worker_name})</span>
                        )}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* QC buttons for completed items */}
      {itemsNeedingQC.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2">
          {itemsNeedingQC.map(item => (
            <ActionButton key={item.id} icon={ClipboardCheck} label={`Run Pre-Trial QC`}
              sublabel={GARMENT_LABELS[item.garment_type] || item.garment_type}
              onClick={() => onQC({ order_item_id: item.id, garment_type: item.garment_type, qc_type: 'pre_trial' })}
              variant="success" testId={`btn-qc-${item.id}`} />
          ))}
        </div>
      )}

      {itemsNeedingQC.length === 0 && production.completed_tasks < production.total_tasks && (
        <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
          Production in progress — {production.completed_tasks}/{production.total_tasks} tasks complete
        </p>
      )}
    </div>
  );
}

/* ============================================================
   STATUS: ready_for_trial
   ============================================================ */
function ReadyForTrialActions({ ctx, order, onAlteration, onRefresh }) {
  const [approving, setApproving] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await alterationsAPI.approveNoAlterations({ order_id: order.id });
      toast.success('Approved — ready for delivery');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2" data-testid="trial-actions">
      <ActionButton icon={CheckCircle2} label="Approved — No Alterations"
        sublabel="Move to ready for delivery"
        onClick={handleApprove} variant="success" testId="btn-approve-trial"
        disabled={approving} />
      <ActionButton icon={Scissors} label="Alterations Needed"
        sublabel="Record trial feedback"
        onClick={onAlteration} testId="btn-record-alterations" />
    </div>
  );
}

/* ============================================================
   STATUS: alterations_in_progress
   ============================================================ */
function AlterationsActions({ ctx, order, onQC, onRefresh }) {
  const { alterations: altCtx } = ctx;
  const activeAlts = altCtx.records.filter(a => !['completed', 'qc_passed'].includes(a.status));
  const completedAlts = altCtx.records.filter(a => a.status === 'completed');

  // Check if any need post-alteration QC
  const needsQC = completedAlts.filter(a => {
    const hasPostQC = ctx.qc.inspections.some(q =>
      q.order_item_id === a.order_item_id && q.qc_type === 'post_alteration' &&
      new Date(q.created_at) > new Date(a.recorded_date || '2000-01-01')
    );
    return !hasPostQC;
  });

  return (
    <div className="space-y-2" data-testid="alterations-actions">
      {activeAlts.length > 0 && (
        <div className="space-y-1">
          {activeAlts.map(a => (
            <div key={a.id} className="flex items-center gap-2 text-xs rounded-lg p-2"
              style={{ backgroundColor: 'rgba(245,158,11,0.06)' }}>
              <Scissors className="w-3 h-3" style={{ color: '#D97706' }} />
              <span style={{ color: 'var(--navy-ink)' }}>
                Trial #{a.trial_number} ({GARMENT_LABELS[a.garment_type] || a.garment_type}) — {a.status.replace('_', ' ')}
              </span>
              {a.assigned_worker_name && <span style={{ color: 'var(--ink-muted)' }}>· {a.assigned_worker_name}</span>}
              <span className="ml-auto tabular-nums" style={{ color: 'var(--ink-muted)' }}>~{a.estimated_hours}h</span>
            </div>
          ))}
        </div>
      )}

      {needsQC.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2">
          {needsQC.map(a => (
            <ActionButton key={a.id} icon={ClipboardCheck} label="Run Post-Alteration QC"
              sublabel={GARMENT_LABELS[a.garment_type] || a.garment_type}
              onClick={() => onQC({ order_item_id: a.order_item_id, garment_type: a.garment_type, qc_type: 'post_alteration' })}
              variant="success" testId={`btn-post-qc-${a.id}`} />
          ))}
        </div>
      )}

      {activeAlts.length === 0 && needsQC.length === 0 && (
        <CompleteSummary text="All alterations completed and QC passed." />
      )}
    </div>
  );
}

/* ============================================================
   STATUS: ready_for_delivery
   ============================================================ */
function ReadyForDeliveryActions({ ctx, cs, order, onPayment, onDelivery }) {
  const { payment } = ctx;
  const hasBalance = payment.balance_due > 0;

  return (
    <div className="flex flex-col sm:flex-row gap-2" data-testid="delivery-actions">
      {hasBalance && (
        <ActionButton icon={CreditCard} label="Collect Balance Payment"
          sublabel={`${cs}${payment.balance_due.toLocaleString('en-IN')} remaining`}
          onClick={onPayment} variant="warning" testId="btn-collect-balance" />
      )}
      <ActionButton icon={Truck} label="Record Delivery"
        sublabel="Capture delivery details"
        onClick={onDelivery} variant="primary" testId="btn-record-delivery" />
    </div>
  );
}

/* ============================================================
   STATUS: delivered
   ============================================================ */
function DeliveredActions({ ctx, cs, order, onPayment }) {
  const { payment } = ctx;
  const hasBalance = payment.balance_due > 0;
  const deliveryDate = order.status_dates?.delivered;

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2" data-testid="delivered-actions">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="w-5 h-5" style={{ color: '#059669' }} />
        <span className="text-sm font-semibold" style={{ color: '#059669' }}>Order Complete</span>
        {deliveryDate && (
          <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
            {new Date(deliveryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        )}
      </div>
      {hasBalance && (
        <div className="flex items-center gap-2 rounded-lg px-3 py-1.5" style={{ backgroundColor: 'rgba(220,38,38,0.06)' }}>
          <AlertTriangle className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
          <span className="text-xs font-bold" style={{ color: '#DC2626' }}>
            Balance Due: {cs}{payment.balance_due.toLocaleString('en-IN')}
          </span>
          <Button size="sm" className="h-7 text-[10px] px-2 rounded-md gap-1"
            style={{ backgroundColor: '#DC2626' }}
            onClick={onPayment} data-testid="btn-overdue-payment">
            <CreditCard className="w-3 h-3" /> Collect
          </Button>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Shared components
   ============================================================ */

function ActionButton({ icon: Icon, label, sublabel, onClick, variant = 'gold', testId, disabled }) {
  const styles = {
    gold: { bg: 'var(--brand-gold)', text: '#fff', hover: 'brightness(1.08)' },
    primary: { bg: 'var(--brand-navy)', text: '#fff', hover: 'brightness(1.15)' },
    success: { bg: '#059669', text: '#fff', hover: 'brightness(1.1)' },
    warning: { bg: '#DC2626', text: '#fff', hover: 'brightness(1.1)' },
  };
  const s = styles[variant] || styles.gold;

  return (
    <button onClick={onClick} disabled={disabled}
      className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-left transition-all hover:shadow-md active:scale-[0.98] disabled:opacity-50"
      style={{ backgroundColor: s.bg, color: s.text, filter: disabled ? 'none' : undefined }}
      data-testid={testId}>
      <Icon className="w-4.5 h-4.5 flex-shrink-0" style={{ width: 18, height: 18 }} />
      <div>
        <p className="text-sm font-bold leading-tight">{label}</p>
        {sublabel && <p className="text-[10px] leading-tight mt-0.5 opacity-80">{sublabel}</p>}
      </div>
    </button>
  );
}

function DoneChip({ label }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl"
      style={{ backgroundColor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
      <CheckCircle2 className="w-4 h-4" style={{ color: '#059669' }} />
      <span className="text-xs font-semibold" style={{ color: '#059669' }}>{label}</span>
    </div>
  );
}

function CompleteSummary({ text }) {
  return (
    <div className="flex items-center gap-2 py-1" data-testid="complete-summary">
      <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#059669' }} />
      <p className="text-xs font-medium" style={{ color: '#059669' }}>{text}</p>
    </div>
  );
}

function ProgressDots({ total, completed }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="w-2 h-2 rounded-full transition-colors"
          style={{ backgroundColor: i < completed ? '#059669' : 'rgba(27,42,74,0.12)' }} />
      ))}
    </div>
  );
}

function CompactTaskList({ taskGroups }) {
  const groups = Object.entries(taskGroups);
  if (groups.length === 0) return null;
  return (
    <div className="text-[10px] px-2 py-1 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)', color: 'var(--ink-muted)' }}>
      {groups.map(([itemId, g]) => (
        <p key={itemId}>{GARMENT_LABELS[g.garment_type] || g.garment_type}: {g.tasks.length} tasks</p>
      ))}
    </div>
  );
}

function CopyWhatsAppButton({ poId }) {
  const [copying, setCopying] = useState(false);
  const handleCopy = async () => {
    setCopying(true);
    try {
      const res = await poAPI.whatsappText(poId);
      await navigator.clipboard.writeText(res.data.text || res.data);
      toast.success('Copied to clipboard!');
    } catch {
      toast.error('Failed to copy');
    } finally {
      setCopying(false);
    }
  };
  return (
    <Button variant="outline" size="sm" className="h-8 text-[10px] gap-1 rounded-lg"
      onClick={handleCopy} disabled={copying} data-testid="btn-copy-whatsapp">
      <Copy className="w-3 h-3" /> WhatsApp
    </Button>
  );
}

/* ============================================================
   Delivery Form (inline in sheet)
   ============================================================ */
function DeliveryForm({ orderId, onComplete }) {
  const [form, setForm] = useState({
    delivery_date: new Date().toISOString().split('T')[0],
    delivery_method: 'pickup',
    received_by: '',
    notes: '',
    tracking_number: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await ordersAPI.deliver(orderId, form);
      toast.success('Order delivered!');
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Delivery Date</label>
        <input type="date" value={form.delivery_date} onChange={e => setForm(p => ({ ...p, delivery_date: e.target.value }))}
          className="mt-1 w-full h-10 px-3 text-sm rounded-lg border" />
      </div>
      <div>
        <label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Method</label>
        <select value={form.delivery_method} onChange={e => setForm(p => ({ ...p, delivery_method: e.target.value }))}
          className="mt-1 w-full h-10 px-3 text-sm rounded-lg border">
          <option value="pickup">Pickup</option>
          <option value="courier">Courier</option>
          <option value="hand_delivered">Hand Delivered</option>
        </select>
      </div>
      <div>
        <label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Received By</label>
        <input value={form.received_by} onChange={e => setForm(p => ({ ...p, received_by: e.target.value }))}
          className="mt-1 w-full h-10 px-3 text-sm rounded-lg border" placeholder="Name of receiver" />
      </div>
      <div>
        <label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Tracking # (international)</label>
        <input value={form.tracking_number} onChange={e => setForm(p => ({ ...p, tracking_number: e.target.value }))}
          className="mt-1 w-full h-10 px-3 text-sm rounded-lg border" placeholder="Optional" />
      </div>
      <div>
        <label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Notes</label>
        <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          className="mt-1 w-full px-3 py-2 text-sm rounded-lg border" rows={2} placeholder="Optional" />
      </div>
      <Button onClick={handleSubmit} disabled={submitting} className="w-full h-10 rounded-xl gap-2"
        style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="btn-submit-delivery">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
        Confirm Delivery
      </Button>
    </div>
  );
}


/* ============================================================
   Inline QC Form (used inside Sheet, not Dialog)
   ============================================================ */
const QC_TYPE_LABELS = { fabric_receipt: 'Fabric Receipt', pre_trial: 'Pre-Trial', post_alteration: 'Post-Alteration' };
const RESULT_CFG = {
  pass: { label: 'Pass', bg: '#059669', icon: CheckCircle2 },
  flag: { label: 'Flag', bg: '#D97706', icon: AlertTriangle },
  fail: { label: 'Fail', bg: '#DC2626', icon: XCircle },
};

function InlineQCForm({ orderId, orderItemId, garmentType, qcType, onComplete }) {
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setLoading(true);
    setNotes('');
    qcAPI.getTemplate(garmentType || '_all', qcType).then(res => {
      const items = (res.data?.checklist_items || []).map(name => ({
        check_name: name, result: null, notes: '',
      }));
      setChecklist(items);
    }).catch(() => setChecklist([])).finally(() => setLoading(false));
  }, [garmentType, qcType]);

  const setItemResult = (idx, result) => {
    setChecklist(prev => { const u = [...prev]; u[idx] = { ...u[idx], result }; return u; });
  };

  const allChecked = checklist.length > 0 && checklist.every(c => c.result);
  const overallResult = !allChecked ? null : checklist.some(c => c.result === 'fail') ? 'fail' : checklist.some(c => c.result === 'flag') ? 'flag' : 'pass';

  const handleSubmit = async () => {
    if (!allChecked) { toast.error('Please check all items'); return; }
    setSubmitting(true);
    try {
      await qcAPI.createInspection({
        order_item_id: orderItemId,
        qc_type: qcType,
        garment_type: garmentType,
        checklist_results: checklist,
        notes: notes || null,
        result: overallResult,
      });
      toast.success(`QC ${QC_TYPE_LABELS[qcType] || qcType}: ${overallResult.toUpperCase()}`);
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit QC');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="py-8 text-center text-xs" style={{ color: 'var(--ink-muted)' }}>Loading checklist...</div>;

  return (
    <div className="space-y-3" data-testid="inline-qc-form">
      <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
        {QC_TYPE_LABELS[qcType] || qcType} · {GARMENT_LABELS[garmentType] || garmentType}
      </p>
      {checklist.map((item, i) => (
        <div key={i} className="flex items-center justify-between py-2 px-2 rounded-lg"
          style={{ backgroundColor: item.result ? `${RESULT_CFG[item.result]?.bg}08` : 'rgba(27,42,74,0.02)' }}>
          <span className="text-sm flex-1 mr-2" style={{ color: 'var(--navy-ink)' }}>{item.check_name}</span>
          <div className="flex gap-1.5">
            {Object.entries(RESULT_CFG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              const selected = item.result === key;
              return (
                <button key={key} onClick={() => setItemResult(i, key)}
                  className="w-9 h-9 rounded-lg flex items-center justify-center transition-all"
                  style={{
                    backgroundColor: selected ? cfg.bg : 'rgba(27,42,74,0.04)',
                    color: selected ? '#fff' : cfg.bg,
                    border: selected ? 'none' : `1px solid ${cfg.bg}30`,
                    transform: selected ? 'scale(1.05)' : 'scale(1)',
                  }}
                  data-testid={`qc-btn-${key}-${i}`}>
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div>
        <label className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>Notes</label>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="mt-1 text-sm" placeholder="Optional notes..." />
      </div>
      {overallResult && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: `${RESULT_CFG[overallResult].bg}10` }}>
          {(() => { const Icon = RESULT_CFG[overallResult].icon; return <Icon className="w-4 h-4" style={{ color: RESULT_CFG[overallResult].bg }} />; })()}
          <span className="text-sm font-bold" style={{ color: RESULT_CFG[overallResult].bg }}>Overall: {RESULT_CFG[overallResult].label}</span>
        </div>
      )}
      <Button onClick={handleSubmit} disabled={!allChecked || submitting} className="w-full h-10 rounded-xl gap-2"
        style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="btn-submit-qc">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Submit QC Result
      </Button>
    </div>
  );
}
